import {
  Article,
  ARTICLE_TYPE,
  ContentItem,
  Gif,
  GIF_TYPE,
  Video,
  VIDEO_TYPE
} from 'interfaces'
import { ComposerPostStatus } from 'services/compose/selectors'
import {
  BRAND_BLUESKY,
  BRAND_FACEBOOK,
  BRAND_GOOGLE,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_PINTEREST,
  BRAND_THREADS,
  BRAND_TIKTOK,
  BRAND_TWITTER,
  BRAND_YOUTUBE,
  POST_TYPE_STORY,
  PostDestination
} from 'shared'
import {
  ComposerBasePost,
  ComposerFacebookPost,
  ComposerGooglePost,
  ComposerInstagramPost,
  ComposerPinterestPost,
  ComposerTikTokPost,
  ComposerTwitterPost,
  ComposerYoutubePost,
  LinkDetails,
  PostDestinationType,
  SocialProfileTag
} from 'shared/types'
import { BlueskyPostPreview } from './components/BlueskyPostPreview'
import { FacebookPostPreview } from './components/FacebookPostPreview'
import { GooglePostPreview } from './components/GooglePostPreview'
import { InstagramPostPreview } from './components/InstagramPostPreview'
import { LinkedinPostPreview } from './components/LinkedinPostPreview'
import { PinterestPostPreview } from './components/PinterestPostPreview'
import { ThreadsPostPreview } from './components/ThreadsPostPreview'
import { TikTokPostPreview } from './components/TikTokPostPreview'
import { TwitterPostPreview } from './components/TwitterPostPreview'
import { YoutubePostPreview } from './components/YoutubePostPreview'
import styles from './PostPreview.pcss'

interface PostPreviewProps {
  postNetwork: PostDestinationType
  post: ComposerBasePost
  tags?: SocialProfileTag[]
  postAsPhoto?: boolean
  imageUrls: string[]
  videoUrl?: string
  videoThumbnailUrl?: string
  content?: ContentItem
  link?: LinkDetails
  comments?: string[]
  twitterPostStatus?: ComposerPostStatus
  featuredImageUrl: string | undefined
  previewProfile?: PostDestination
  className?: string
  reshare?: boolean
  textBackgroundUrl?: string
  textColor?: string
  documentName?: string
  igPhotoTags?: string[]
  igCoverUrl?: string
  igOnChangeCover?: () => void
}

export function PostPreview(props: PostPreviewProps) {
  const statusText = props.post?.status.text || ''
  const contentType = props.content?.type
  const featuredImageUrl = props.featuredImageUrl
    || (props.content as any)?.imageUrl
    || props.imageUrls[0]
    || props.link?.selectedImageUrl
    || props.link?.image[0]

  const isTwitterGif = contentType === GIF_TYPE && props.content?.feed.type === BRAND_TWITTER
  const isVideoPost = Boolean(props.videoUrl) || contentType === VIDEO_TYPE || isTwitterGif
  const videoUrl = isTwitterGif ? (props.content as Gif).gifUrl : props.videoUrl
  const originalPostUrl = props.content?.socialLink || (props.content as any)?.sourceLink
  const originalPostLink = originalPostUrl
    ? <div className={styles.link}><a href={originalPostUrl} target="_blank" rel="noopener noreferrer">See original</a></div>
    : null

  let linkData: { url: string, title: string, description?: string, isVideo?: boolean } | undefined
  if (contentType === ARTICLE_TYPE) {
    linkData = {
      url: (props.content as Article).sourceLink || props.content?.socialLink || '',
      description: (props.content as Article).description,
      title: (props.content as Article).title
    }
  } else if (contentType === VIDEO_TYPE) {
    linkData = {
      url: (props.content as Video).shortLink || props.content?.socialLink || '',
      description: (props.content as Video).description,
      title: (props.content as Video).title,
      isVideo: true
    }
  } else if (props.link) {
    linkData = {
      url: props.link.domain || props.link.url,
      description: props.link.description,
      title: props.link.title
    }
  }

  if (props.postAsPhoto || !linkData?.url) {
    linkData = undefined
  }

  const wrapperClass = props.className || ''

  switch (props.postNetwork) {
    case BRAND_FACEBOOK: {
      const post = props.post as ComposerFacebookPost
      // EXPL: Stories are published without a cover image
      const isStory = post.postType === POST_TYPE_STORY
      return (
        <div className={wrapperClass}>
          <FacebookPostPreview
            profile={props.previewProfile}
            statusText={statusText}
            tags={props.tags}
            article={linkData}
            featuredImageUrl={featuredImageUrl}
            imageUrls={props.imageUrls}
            videoUrl={videoUrl}
            isVideoPost={isVideoPost || isTwitterGif}
            contentItem={props.content}
            reshare={props.reshare}
            isStory={isStory}
            isReel={post.postType === 'reels'}
            commentsCount={props.comments?.length || 0}
            textBackgroundUrl={props.textBackgroundUrl}
            textColor={props.textColor}
            videoThumbnailUrl={isStory ? undefined : props.videoThumbnailUrl}
          />
          {originalPostLink}
        </div>
      )
    }

    case BRAND_TWITTER: {
      const post = props.post as ComposerTwitterPost
      const articleUrlMissing = props.twitterPostStatus?.caption.hints.includes('composer.hints.twitter.no-article-url')

      return (
        <div className={wrapperClass}>
          <TwitterPostPreview
            profile={props.previewProfile}
            status={statusText}
            tweetText={props.content?.tweetText}
            quote={post.tweetType === 'quote'}
            retweet={post.tweetType === 'retweet'}
            featuredImageUrl={articleUrlMissing ? undefined : featuredImageUrl}
            imageUrls={props.imageUrls}
            article={articleUrlMissing ? undefined : linkData}
            videoUrl={videoUrl}
            isVideoPost={isVideoPost}
          />
          {originalPostLink}
        </div>
      )
    }

    case BRAND_BLUESKY: {
      const articleUrlMissing = props.twitterPostStatus?.caption.hints.includes('composer.hints.twitter.no-article-url')
      return (
        <div className={wrapperClass}>
          <BlueskyPostPreview
            profile={props.previewProfile}
            status={statusText}
            featuredImageUrl={articleUrlMissing ? undefined : featuredImageUrl}
            imageUrls={props.imageUrls}
            commentsCount={props.comments?.length || 0}
            article={articleUrlMissing ? undefined : linkData}
            videoUrl={videoUrl}
          />
          {originalPostLink}
        </div>
      )
    }

    case BRAND_INSTAGRAM: {
      const post = props.post as ComposerInstagramPost
      // EXPL: Stories are published without a cover image
      const isStory = post.postType === POST_TYPE_STORY
      return (
        <div className={wrapperClass}>
          <InstagramPostPreview
            profile={props.previewProfile}
            statusText={statusText}
            comments={props.comments}
            featuredImageUrl={featuredImageUrl}
            imageUrls={props.imageUrls}
            isLink={Boolean(linkData)}
            videoUrl={videoUrl}
            type={post.postType as any}
            location={Object.values(post.locations || {})[0]?.name}
            videoThumbnailUrl={isStory ? undefined : (props.igCoverUrl || props.videoThumbnailUrl)}
            photoTags={props.igPhotoTags}
            onChangeCover={isStory ? undefined : props.igOnChangeCover}
          />
          {originalPostLink}
        </div>
      )
    }

    case BRAND_THREADS: {
      return (
        <div className={wrapperClass}>
          <ThreadsPostPreview
            profile={props.previewProfile}
            statusText={statusText}
            featuredImageUrl={featuredImageUrl}
            imageUrls={props.imageUrls}
            videoUrl={videoUrl}
            link={linkData}
          />
          {originalPostLink}
        </div>
      )
    }

    case BRAND_PINTEREST: {
      const post = props.post as ComposerPinterestPost
      return (
        <div className={wrapperClass}>
          <PinterestPostPreview
            text={post.title}
            videoUrl={videoUrl}
            imageUrl={featuredImageUrl}
          />
          {originalPostLink}
        </div>
      )
    }

    case BRAND_LINKEDIN: {
      return (
        <div className={wrapperClass}>
          <LinkedinPostPreview
            profile={props.previewProfile}
            statusText={statusText}
            featuredImageUrl={featuredImageUrl}
            imageUrls={props.imageUrls}
            videoUrl={videoUrl}
            isVideoPost={isVideoPost}
            linkData={linkData}
            documentName={props.documentName}
          />
          {originalPostLink}
        </div>
      )
    }

    case BRAND_TIKTOK: {
      const post = props.post as ComposerTikTokPost
      return (
        <div className={wrapperClass}>
          <TikTokPostPreview
            profile={props.previewProfile}
            videoUrl={videoUrl}
            imageUrls={props.imageUrls}
            featuredImageUrl={featuredImageUrl}
            videoThumbnailUrl={props.videoThumbnailUrl}
            status={props.post.status.text}
            title={post.title}
            description={post.description}
          />
          {originalPostLink}
        </div>
      )
    }

    case BRAND_GOOGLE: {
      const post = props.post as ComposerGooglePost
      return (
        <div className={wrapperClass}>
          <GooglePostPreview
            profile={props.previewProfile}
            featuredImageUrl={featuredImageUrl}
            statusText={post.status.text}
            buttonLink={post.update.link}
            buttonType={post.update.buttonType}
          />
          {originalPostLink}
        </div>
      )
    }

    case BRAND_YOUTUBE: {
      const post = props.post as ComposerYoutubePost
      return (
        <div className={wrapperClass}>
          <YoutubePostPreview
            profile={props.previewProfile}
            videoUrl={videoUrl}
            title={post.title}
          />
          {originalPostLink}
        </div>
      )
    }

    default:
      return null
  }
}

export default PostPreview
